// client side slice

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ReaderTaskOrchestrationDoneAction,
  ReaderTaskOrchestrationLoadAction,
  ReaderTaskOrchestrationStartNextSubTaskAction,
  readerTaskOrchestrationState,
} from './readerTaskOrchestration.types';
import { RootStateWithInjection } from '../store';
import { createSliceHook, makeActionCreator } from '../helpers';

const initialState: readerTaskOrchestrationState = {
  loadingState: 'initial',
  spinner: false,
  clinicalTrialId: '',
  taskId: '',
  subTaskId: '',
  data: null,
};

const { actEmpty } = makeActionCreator<readerTaskOrchestrationState>();

export const readerTaskOrchestrationSlice = createSlice({
  name: 'readerTaskOrchestration',
  initialState,
  reducers: {
    start: (
      state,
      action: PayloadAction<ReaderTaskOrchestrationLoadAction['payload']>
    ) => {
      state.loadingState = 'loading';
      state.spinner = true;
      state.clinicalTrialId = action.payload.clinicalTrialId;
      state.taskId = action.payload.taskId;
      state.subTaskId = action.payload.subTaskId;
    },
    load: (
      state,
      action: PayloadAction<ReaderTaskOrchestrationLoadAction['payload']>
    ) => {
      state.loadingState = 'loading';
      state.spinner = true;
      state.clinicalTrialId = action.payload.clinicalTrialId;
      state.taskId = action.payload.taskId;
      state.subTaskId = action.payload.subTaskId;
    },
    done: (
      state,
      action: PayloadAction<ReaderTaskOrchestrationDoneAction['payload']>
    ) => {
      state.data = action.payload;
      state.loadingState = 'done';
      state.spinner = false;
    },
    error: state => {
      state.loadingState = 'error';
      state.spinner = false;
    },
    setLoadingState: (
      state,
      action: PayloadAction<readerTaskOrchestrationState['loadingState']>
    ) => {
      state.loadingState = action.payload;
      state.spinner = action.payload === 'loading';
    },
    setSpinner: (state, action: PayloadAction<boolean>) => {
      state.spinner = action.payload;
    },
    startNextSubTask:
      actEmpty<ReaderTaskOrchestrationStartNextSubTaskAction['payload']>(),
    reset: state => {
      state.loadingState = 'initial';
      state.spinner = false;
      state.clinicalTrialId = '';
      state.data = null;
      state.taskId = '';
      state.subTaskId = '';
    },
  },
});

export const {
  done,
  error,
  start,
  load,
  reset,
  startNextSubTask,
  setLoadingState,
} = readerTaskOrchestrationSlice.actions;

export const readerViewerPageSelector = (
  state: RootStateWithInjection<[typeof readerTaskOrchestrationSlice]>
) => {
  return (
    state.readerTaskOrchestration ??
    readerTaskOrchestrationSlice.getInitialState()
  );
};

export const useReaderTaskOrchestrationSlice = createSliceHook(
  readerTaskOrchestrationSlice.name,
  readerTaskOrchestrationSlice.actions
);
